import { PART_NUMBERS, NODE_TYPES } from './partNumbers.jsx';
import { normalizeLegacyInterfaceOption } from './migration.jsx';
import { getNetworkOption } from './networkPorts';
import { getVendor } from '../config/vendorRegistry';
import { resolveFlashNodeModel } from './flashNodeModel';

/*********************************************************************************
 *                           NODE PART NUMBER UTILITIES
 *********************************************************************************/

/**
 * Get the correct node part number based on node type and configuration.
 * CONF-210: the flash node is the one type whose SKU depends on the config
 * (afaNodeModel), resolved through resolveFlashNodeModel so BOM, pricing and
 * metrics all book the same node.
 */
export const getNodePartNumber = (nodeType, config) => {
  switch (nodeType) {
    case NODE_TYPES.DIRECTOR:
      return PART_NUMBERS.NODES.DIRECTOR;

    case NODE_TYPES.HYBRID_STORAGE:
      return PART_NUMBERS.NODES.HYBRID_STORAGE;

    case NODE_TYPES.AFA_STORAGE:
      return resolveFlashNodeModel(config).partNumber;

    case NODE_TYPES.HYBRID_DUAL_ACTUATOR:
        return PART_NUMBERS.NODES.HYBRID_DUAL_ACTUATOR;

    default:
      throw new Error(`Unknown node type: ${nodeType}`);
  }
};

/**
 * Get all active node part numbers for the current configuration
 * Returns only the nodes that are enabled in the config
 */
export const getActiveNodePartNumbers = (config) => {
  // MORK TODO : For Expansion and BaseSystem - if Directors/Velo Count is 0, do we include Director part numbers?
  const activeNodes = {
    [NODE_TYPES.DIRECTOR]: {
      partNumber: getNodePartNumber(NODE_TYPES.DIRECTOR),
      count: config.veloCount
    }
  };

  if (config.hybridEnabled) {
    activeNodes[NODE_TYPES.HYBRID_STORAGE] = {
      partNumber: getNodePartNumber(NODE_TYPES.HYBRID_STORAGE),
      count: config.hybridNodeCount
    };
  }

  if (config.afaEnabled) {
    activeNodes[NODE_TYPES.AFA_STORAGE] = {
      partNumber: getNodePartNumber(NODE_TYPES.AFA_STORAGE, config),
      count: config.afaNodeCount
    };
  }

  return activeNodes;
};

/**
 * Get node part numbers for all possible configurations (for validation/testing)
 */
export const getAllNodePartNumbers = (config) => ({
  [NODE_TYPES.DIRECTOR]: getNodePartNumber(NODE_TYPES.DIRECTOR, config),
  [NODE_TYPES.HYBRID_STORAGE]: getNodePartNumber(NODE_TYPES.HYBRID_STORAGE, config),
  [NODE_TYPES.AFA_STORAGE]: getNodePartNumber(NODE_TYPES.AFA_STORAGE, config)
});

/*********************************************************************************
 *                         SOFTWARE PART NUMBER UTILITIES
 *********************************************************************************/
/**
 * Get VduraCare part numbers based on service option and active vendor.
 * For Dell, maps Dell-specific service option keys to Dell SKUs.
 * For AIC (default), maps AIC service option keys to AIC SKUs.
 * ssd_support / hdd_support are null for options with no HW support child (e.g. Dell Software Only).
 */
// Classic licensing: 10TB blocks priced per month. Term-based vendors declare
// {unitTB: 1, termBased: true} on the serviceOption; the SKU's unit price then
// covers the whole term (no month multiplier anywhere downstream).
const DEFAULT_SW_MODEL = { unitTB: 10, termBased: false };

const findServiceOption = (serviceOption, vendor) =>
  vendor?.capabilities?.serviceOptions?.find(o => o.key === serviceOption);

export const getSoftwareLicensingModel = (serviceOption, vendor) =>
  findServiceOption(serviceOption, vendor)?.licensing ?? DEFAULT_SW_MODEL;

// Snap a requested duration DOWN to the nearest vendor-declared one
// (below the minimum -> the minimum). Durations must be a non-empty array.
export const snapToDuration = (months, durations) => {
  const atOrBelow = durations.filter(m => m <= (months ?? 0));
  return atOrBelow.length ? Math.max(...atOrBelow) : Math.min(...durations);
};

const resolveSkuValue = (value, vendor, subscriptionMonths) => {
  if (value === null || typeof value === 'string') return value;
  // per-duration map: {12: 'SKU-1YR', 36: 'SKU-3YR', 60: 'SKU-5YR'}
  const durations = Object.keys(value).map(Number);
  return value[snapToDuration(subscriptionMonths, durations)];
};

export const getServicePartNumbers = (serviceOption, vendor, subscriptionMonths) => {
  const vendorId = vendor?.id || 'AIC';

  // Registry-declared SKUs win (SMC and all future vendors). Vendors that
  // declare none (AIC, DELL) fall through to the code switches below —
  // migrating them onto registry data is the deferred consolidation epic.
  const declared = findServiceOption(serviceOption, vendor)?.skus;
  if (declared) {
    // Full literal, not a keyed loop: TS infers a loop-built object as `{}`,
    // which poisons this function's return union for TS consumers (pricingRules.ts).
    return {
      ssd: resolveSkuValue(declared.ssd ?? null, vendor, subscriptionMonths),
      hdd: resolveSkuValue(declared.hdd ?? null, vendor, subscriptionMonths),
      ssd_license: resolveSkuValue(declared.ssd_license ?? null, vendor, subscriptionMonths),
      ssd_support: resolveSkuValue(declared.ssd_support ?? null, vendor, subscriptionMonths),
      hdd_license: resolveSkuValue(declared.hdd_license ?? null, vendor, subscriptionMonths),
      hdd_support: resolveSkuValue(declared.hdd_support ?? null, vendor, subscriptionMonths),
    };
  }

  if (vendorId === 'DELL') {
    switch (serviceOption) {
      case 'dellNbdMedia':
        return {
          ssd: 'VDP-VDURACare-10-DELL-HP-Media',
          hdd: 'VDP-VDURACare-10-DELL-C-Media',
          ssd_license: null,
          ssd_support: 'HW-Support-HP-NBD',
          hdd_license: null,
          hdd_support: 'HW-Support-C-NBD'
        };
      case 'dellNoReturnMedia':
        return {
          ssd: 'VDP-VDURACare-10-DELL-HP-NR-Media',
          hdd: 'VDP-VDURACare-10-DELL-C-NR-Media',
          ssd_license: null,
          ssd_support: 'HW-Support-HP-NBD-NR-Media',
          hdd_license: null,
          hdd_support: 'HW-Support-C-NBD-NR-Media'
        };
      default: // 'dellSoftwareOnly'
        return {
          ssd: 'VDP-VDURACare-10-DELL-HP-SW',
          hdd: 'VDP-VDURACare-10-DELL-C-SW',
          ssd_license: null,
          ssd_support: null,
          hdd_license: null,
          hdd_support: null
        };
    }
  }

  // AIC (and any future vendor that hasn't overridden)
  switch (serviceOption) {
    case 'noReturnMedia':
      return {
        ssd: PART_NUMBERS.SOFTWARE.VDURA_CARE.SSD_NO_RETURN_MEDIA,
        hdd: PART_NUMBERS.SOFTWARE.VDURA_CARE.HDD_NO_RETURN_MEDIA,
        ssd_license: PART_NUMBERS.SOFTWARE.VDURA_CARE_LICENSE.SSD_NO_RETURN_MEDIA,
        ssd_support: PART_NUMBERS.SOFTWARE.VDURA_CARE_HW_SUPPORT.SSD_NO_RETURN_MEDIA,
        hdd_license: PART_NUMBERS.SOFTWARE.VDURA_CARE_LICENSE.HDD_NO_RETURN_MEDIA,
        hdd_support: PART_NUMBERS.SOFTWARE.VDURA_CARE_HW_SUPPORT.HDD_NO_RETURN_MEDIA
      };
    case 'noReturnHardware':
      return {
        ssd: PART_NUMBERS.SOFTWARE.VDURA_CARE.SSD_NO_RETURN_HARDWARE,
        hdd: PART_NUMBERS.SOFTWARE.VDURA_CARE.HDD_NO_RETURN_HARDWARE,
        ssd_license: PART_NUMBERS.SOFTWARE.VDURA_CARE_LICENSE.SSD_NO_RETURN_HARDWARE,
        ssd_support: PART_NUMBERS.SOFTWARE.VDURA_CARE_HW_SUPPORT.SSD_NO_RETURN_HARDWARE,
        hdd_license: PART_NUMBERS.SOFTWARE.VDURA_CARE_LICENSE.HDD_NO_RETURN_HARDWARE,
        hdd_support: PART_NUMBERS.SOFTWARE.VDURA_CARE_HW_SUPPORT.HDD_NO_RETURN_HARDWARE
      };
    default: // 'standard'
      return {
        ssd: PART_NUMBERS.SOFTWARE.VDURA_CARE.SSD_STANDARD,
        hdd: PART_NUMBERS.SOFTWARE.VDURA_CARE.HDD_STANDARD,
        ssd_license: PART_NUMBERS.SOFTWARE.VDURA_CARE_LICENSE.SSD_STANDARD,
        ssd_support: PART_NUMBERS.SOFTWARE.VDURA_CARE_HW_SUPPORT.SSD_STANDARD,
        hdd_license: PART_NUMBERS.SOFTWARE.VDURA_CARE_LICENSE.HDD_STANDARD,
        hdd_support: PART_NUMBERS.SOFTWARE.VDURA_CARE_HW_SUPPORT.HDD_STANDARD
      };
  }
};

// Backward-compatible alias — callers that don't yet have vendor context still work
export const getVduraCarePartNumbers = (serviceOption) => getServicePartNumbers(serviceOption, null);

/**
 * Get software discount part number
 */
export const getDiscountPartNumber = () => {
  return PART_NUMBERS.SOFTWARE.DISCOUNT;
};

/**
 * Get all active software part numbers for the current configuration.
 * Pass vendor so Dell service option keys resolve to Dell SKUs.
 */
export const getActiveSoftwarePartNumbers = (config, vendor) => {
  const vduraCarePartNumbers = getServicePartNumbers(config.serviceOption, vendor, config.subscriptionMonths);
  
  const activeSoftware = {
    vduraCare: {
      ssdPartNumber: vduraCarePartNumbers.ssd
    },
    vduraCareSubcomponents: {
      ssd_license: vduraCarePartNumbers.ssd_license,
      ssd_support: vduraCarePartNumbers.ssd_support
    }
  };

  // Only include HDD VduraCare if hybrid is enabled and has HDDs
  if (config.hybridEnabled && config.hybridHddCapacity > 0) {
    // MORK TODO : these part numbers aren't previously defined and only get defined if hybrid is enabled. This could be problematic in the future.
    activeSoftware.vduraCare.hddPartNumber = vduraCarePartNumbers.hdd;
    activeSoftware.vduraCareSubcomponents.hdd_license = vduraCarePartNumbers.hdd_license;
    activeSoftware.vduraCareSubcomponents.hdd_support = vduraCarePartNumbers.hdd_support;
  }

  // Add discount partnumber regardless of discount months - other logic will handle if it actually applies
  activeSoftware.discount = getDiscountPartNumber();
  

  return activeSoftware;
};

/**
 * Get all possible software part numbers (for validation/testing)
 */
export const getAllSoftwarePartNumbers = () => ({
  vduraCare: {
    ssd: {
      standard: PART_NUMBERS.SOFTWARE.VDURA_CARE.SSD_STANDARD,
      noReturnMedia: PART_NUMBERS.SOFTWARE.VDURA_CARE.SSD_NO_RETURN_MEDIA,
      noReturnHardware: PART_NUMBERS.SOFTWARE.VDURA_CARE.SSD_NO_RETURN_HARDWARE
    },
    hdd: {
      standard: PART_NUMBERS.SOFTWARE.VDURA_CARE.HDD_STANDARD,
      noReturnMedia: PART_NUMBERS.SOFTWARE.VDURA_CARE.HDD_NO_RETURN_MEDIA,
      noReturnHardware: PART_NUMBERS.SOFTWARE.VDURA_CARE.HDD_NO_RETURN_HARDWARE
    }
  },
  vduraCareSubcomponents: {
    ssd_license: {
      standard: PART_NUMBERS.SOFTWARE.VDURA_CARE_LICENSE.SSD_STANDARD,
      noReturnMedia: PART_NUMBERS.SOFTWARE.VDURA_CARE_LICENSE.SSD_NO_RETURN_MEDIA,
      noReturnHardware: PART_NUMBERS.SOFTWARE.VDURA_CARE_LICENSE.SSD_NO_RETURN_HARDWARE
    },
    hdd_license: {
      standard: PART_NUMBERS.SOFTWARE.VDURA_CARE_LICENSE.HDD_STANDARD,
      noReturnMedia: PART_NUMBERS.SOFTWARE.VDURA_CARE_LICENSE.HDD_NO_RETURN_MEDIA,
      noReturnHardware: PART_NUMBERS.SOFTWARE.VDURA_CARE_LICENSE.HDD_NO_RETURN_HARDWARE
    },
    ssd_support: {
      standard: PART_NUMBERS.SOFTWARE.VDURA_CARE_HW_SUPPORT.SSD_STANDARD,
      noReturnMedia: PART_NUMBERS.SOFTWARE.VDURA_CARE_HW_SUPPORT.SSD_NO_RETURN_MEDIA,
      noReturnHardware: PART_NUMBERS.SOFTWARE.VDURA_CARE_HW_SUPPORT.SSD_NO_RETURN_HARDWARE
    },
    hdd_support: {
      standard: PART_NUMBERS.SOFTWARE.VDURA_CARE_HW_SUPPORT.HDD_STANDARD,
      noReturnMedia: PART_NUMBERS.SOFTWARE.VDURA_CARE_HW_SUPPORT.HDD_NO_RETURN_MEDIA,
      noReturnHardware: PART_NUMBERS.SOFTWARE.VDURA_CARE_HW_SUPPORT.HDD_NO_RETURN_HARDWARE
    }
  },
  discount: PART_NUMBERS.SOFTWARE.DISCOUNT
});


/*********************************************************************************
 * 
 *                    HARDWARE PART NUMBER UTILITIES (drives/jbods)
 * 
 * 
 * NOTE: These functions are old and can be improved upon as we have more config
 *       options and UI improvements. They are actively used.
 *********************************************************************************/
/**
 * Generate SSD part number from capacity
 * Replaces the getSsdPartNumber logic from generateBom.jsx
 */
export const generateSsdPartNumber = (capacity) => {
  const capacityStr = capacity.toString();
  const [wholePart, decimalPart] = capacityStr.split('.');
  const firstDecimalDigit = decimalPart ? decimalPart[0] : '0';
  return `${PART_NUMBERS.STORAGE.SSD_BASE}${wholePart}.${firstDecimalDigit}`;
};

/**
 * Get SSD part number for specific storage type with fallback logic
 * Replaces getAfaSsdPartNumber and getHybridSsdPartNumber from generateBom.jsx
 */
export const getStorageSsdPartNumber = (config, storageType, productDb, vendorId = 'AIC') => {
  const configKey = storageType === 'afa' ? 'afaSsdPartNumber' : 'hybridSsdPartNumber';
  const capacityKey = storageType === 'afa' ? 'afaVpodSsdCapacity' : 'hybridVpodSsdCapacity';

  // If user selected a specific part number, use it — but only when the active
  // vendor's book knows that SKU (CONF-215). A stored SKU can be stale: DELL and
  // SMC sell SED drives as "VCH-NVME-15.3s" where AIC sells "VCH-NVME-15.3", so
  // a SKU carried across a vendor switch passed through unchecked and put a line
  // with no description and no price on the BOM/CSV. Re-resolve by capacity.
  const storedPartNumber = config[configKey];
  const products = productDb?.products;
  if (storedPartNumber && (!products || products[storedPartNumber])) {
    return storedPartNumber;
  }

  // SP4: the registry is the source of truth for what is offered. productDb
  // carried a second copy that had already drifted — it still listed
  // VCH-NVME-61.4 after CONF-101 pulled it for stock — so a config falling
  // through here resolved to a withdrawn drive.
  //
  // Not price-neutral at 61.44TB: both copies list two entries there and
  // disagree on which survives, so this .find now returns VCH-NVME-61.4-QLC
  // where productDb returned VCH-NVME-61.4. Only a config with no explicit
  // afaSsdPartNumber reaches here, so the effect is confined to repricing a
  // saved quote that predates the UI storing part numbers.
  //
  // Note this is the one reader of these mappings that does NOT filter by
  // availableFrom — storageRules.ts, BaselineConfigCard.jsx and
  // AIConfigOptimizer.jsx all do. Aligning it is a separate ticket: it would
  // move legacy 61.44 quotes to a third SKU and needs its own decision.
  const mappingKey = storageType === 'afa' ? 'afaSsd' : 'hybridSsd';
  const mapping = getVendor(vendorId).capabilities.productMappings?.[mappingKey]
    ?.find((m) => m.capacity === config[capacityKey]);
  if (mapping) {
    return mapping.partNumber;
  }

  // Fallback to generated part number
  return generateSsdPartNumber(config[capacityKey]);
};

/**
 * Generate HDD JBOD part number from configuration
 * Replaces the HDD part number logic from generateBom.jsx
 */
export const generateHddPartNumber = (config) => {
  // Prefer explicit part number stored in config by the UI (set from product mapping on selection).
  // This decouples the SKU from the jbodSize × driveSize formula, which breaks when the chassis
  // model differs from the installed drive count (e.g. 30 drives in a J78 chassis → VCH-5000-J78-480).
  if (config.hybridHddPartNumber) {
    return config.hybridHddPartNumber;
  }

  // Fallback formula for legacy saved configs that pre-date explicit partNumber storage.
  if (!config.hybridEnabled || config.hybridHddCapacity === 0) {
    return `${PART_NUMBERS.STORAGE.HDD_BASE}${config.jbodSize}-0`;
  }
  const totalCapacity = config.jbodSize * config.hybridHddCapacity;
  return `${PART_NUMBERS.STORAGE.HDD_BASE}${config.jbodSize}-${totalCapacity}`;
};

/**
 * Get all active storage drive part numbers
 */
export const getActiveStorageDrivePartNumbers = (config, productDb, vendorId = 'AIC') => {
  const drives = {
    director: {
      ssdPartNumber: generateSsdPartNumber(config.veloSsdCapacity),
      count: config.veloCount * productDb.ssdPerVelo // 2 SSDs per director (MORK TODO : should reference either metricsDb or vendorRegistry)
    }
  };

  if (config.hybridEnabled) {
    drives.hybrid = {
      ssdPartNumber: getStorageSsdPartNumber(config, 'hybrid', productDb, vendorId),
      ssdCount: config.hybridNodeCount * productDb.ssdPerVpod, // 12 SSDs per hybrid node
    };

    // Only add HDD if capacity > 0
    // NOTE : HDD partnumber is actually a JBOD partnumber filled with HDDs.
    if (config.hybridHddCapacity > 0) {
      drives.hybrid.hddPartNumber = generateHddPartNumber(config);
      drives.hybrid.hddCount = config.hybridNodeCount; // 1 JBOD per hybrid node
    }
  }

  if (config.afaEnabled) {
    drives.afa = {
      ssdPartNumber: getStorageSsdPartNumber(config, 'afa', productDb, vendorId),
      ssdCount: config.afaNodeCount * productDb.ssdPerVpod // 12 SSDs per AFA node
    };
  }

  return drives;
};


/*********************************************************************************
 *                       NETWORK CARD PART NUMBER UTILITIES
 *********************************************************************************/
/**
 * The option a caller with NO vendor context gets. Production has such callers
 * — `getAllUniquePartNumbers` (below) is reached from App.js without a vendor,
 * and `getNetworkOption` can only fall back within the capabilities it is
 * given — so this must degrade rather than throw. Its part numbers are the
 * pre-CONF-93 Ethernet constants, i.e. exactly what those callers resolved
 * before, which is what keeps them BOM-neutral.
 */
const FALLBACK_NETWORK_OPTION = {
  id: '100GbE',
  portsPerNic: 2,
  partNumbers: {
    ocp: PART_NUMBERS.NETWORK.ETHERNET.STORAGE_OCP,
    pcie: PART_NUMBERS.NETWORK.ETHERNET.STORAGE_PCIE,
  },
};

/**
 * Get network interface part numbers for the selected network option.
 *
 * CONF-93: the cards come from `capabilities.networkOptions`, not from a
 * branch on the interface string. The old branch tested for 'Infiniband',
 * which `normalizeLegacyInterfaceOption` stopped returning — so every option
 * silently resolved to the Ethernet cards.
 */
export const getNetworkPartNumbers = (interfaceOption, capabilities) => {
  const option = getNetworkOption(capabilities, normalizeLegacyInterfaceOption(interfaceOption))
    ?? FALLBACK_NETWORK_OPTION;

  // CONF-45 override, still live. Directors take the NDR200 OCP card on the
  // Broadcom path regardless of the Ethernet selection. Migration maps the old
  // 'Ethernet' value to '100GbE', so keeping this is what makes that migration
  // BOM-neutral. Removing it changes the hardware on every existing quote and
  // needs its own ticket.
  const directorOcp = option.id === '100GbE'
    ? PART_NUMBERS.NETWORK.ETHERNET.DIRECTOR_OCP
    : option.partNumbers.ocp;

  return {
    directorOcp,
    // Single-port options put two cards in a director (CCI "2x SP" bundles).
    directorPcie: option.portsPerNic === 1 ? option.partNumbers.pcie : null,
    storagePcie: option.partNumbers.pcie,
    storageOcp: option.partNumbers.ocp,
  };
};

/**
 * Get all active network card part numbers and counts.
 *
 * `director` is `{ ocp, pcie? }`: the PCIe block is present only for
 * single-port options, which ship two cards per director.
 */
export const getActiveNetworkPartNumbers = (config, capabilities) => {
  const networkParts = getNetworkPartNumbers(config.interfaceOption, capabilities);

  const activeNetwork = {
    director: {
      ocp: { partNumber: networkParts.directorOcp, count: config.veloCount },
    },
  };

  if (networkParts.directorPcie) {
    activeNetwork.director.pcie = { partNumber: networkParts.directorPcie, count: config.veloCount };
  }

  // Hybrid storage nodes take 1 PCIe + 1 OCP per node. Flash nodes take
  // whatever their model says (CONF-210: the Dual CPU node takes 2 PCIe +
  // 1 OCP), so the counts here are cards, not nodes — consumers must not
  // multiply by node count again.
  const storageBlock = (nodes, layout = { pcie: 1, ocp: 1 }) => ({
    pcie: { partNumber: networkParts.storagePcie, count: nodes * layout.pcie },
    ocp: { partNumber: networkParts.storageOcp, count: nodes * layout.ocp },
  });

  if (config.hybridEnabled) {
    activeNetwork.hybrid = storageBlock(config.hybridNodeCount);
  }
  if (config.afaEnabled) {
    activeNetwork.afa = storageBlock(config.afaNodeCount, resolveFlashNodeModel(config).nicLayout);
  }

  return activeNetwork;
};

/*********************************************************************************
 *                    INSTALLATION SERVICE PART NUMBER UTILITIES
 *********************************************************************************/
/**
 * Get installation service part number
 */
export const getInstallationPartNumber = () => {
  return PART_NUMBERS.SERVICES.INSTALLATION;
};

/**
 * Calculate installation service count based on rack units
 * Installation is charged per rack (42 RU per rack)
 * NOTE : 42 is a magic number that could change in the future
 */
export const getInstallationCount = (totalRackUnits) => {
  return Math.ceil(totalRackUnits / 42);
};

/**
 * Get all active services part numbers and counts
 */
export const getActiveServicesPartNumbers = (config, metrics) => {
  const services = {
    installation: {
      partNumber: getInstallationPartNumber(),
      count: getInstallationCount(metrics.totalRU)
    }
  };

  return services;
};

/**
 * Get all possible services part numbers (for validation/testing)
 */
export const getAllServicesPartNumbers = () => ({
  installation: PART_NUMBERS.SERVICES.INSTALLATION
});




/*********************************************************************************
 * Master function to get all part numbers and counts for a configuration
 * Single source of truth for all system components
 * 
 * @param {Object} config - System configuration
 * @param {Object} productDb - Product database with specifications
 * @param {Object} metrics - Calculated system metrics (needed for installation count)
 * @param {Object} [vendor] - Vendor. Still OPTIONAL for legacy callers, but
 *   EVERY production caller now passes it: `generateSwBom` (generateBom.jsx:8),
 *   `generateHwBom` (:190), `calculateSoftwarePricing` (pricingCalculator.jsx:40),
 *   `calculateHardwarePricing` (:133) and `getAllUniquePartNumbers` (below).
 *   The doc here used to claim the pricing pair did not — that was stale, they
 *   have threaded it for some time — and `getAllUniquePartNumbers` was the last
 *   real gap until CONF-93 closed it.
 *
 *   It still matters that the parameter is optional, because omitting it is
 *   silently wrong rather than loud: the `|| 'AIC'` below resolves a DELL
 *   config's drives through AIC's registry, `getActiveSoftwarePartNumbers`'s own
 *   `vendor?.id || 'AIC'` (:105) hands a DELL quote AIC's VduraCare subcomponent
 *   SKUs, and since CONF-93 there are no `networkOptions` to resolve against, so
 *   the NIC selection degrades to the pre-CONF-93 Ethernet cards
 *   (FALLBACK_NETWORK_OPTION above). Making it required is
 *   docs/superpowers/backlog/2026-08-11-sp4-follow-ups.md item 1.
 * @returns {Object} Complete part number structure with counts
 *********************************************************************************/
export const getAllActivePartNumbers = (config, productDb, metrics, vendor) => {
  return {
    nodes: getActiveNodePartNumbers(config),
    drives: getActiveStorageDrivePartNumbers(config, productDb, vendor?.id || 'AIC'),
    network: getActiveNetworkPartNumbers(config, vendor?.capabilities),
    software: getActiveSoftwarePartNumbers(config, vendor),
    services: getActiveServicesPartNumbers(config, metrics)
  };
};

/**
 * Helper function to get a flat list of all unique part numbers
 * Useful for bulk pricing lookups
 *
 * `vendor` is threaded (CONF-93). Its one production caller, App.js's
 * ExecPricingView `activePartNumbers` Set, HAS a selected vendor; it just used
 * to drop it here. That made the exec panel's Set disagree with the BOM on
 * every network option but 100GbE — the Set held the Ethernet NICs (the
 * vendor-less FALLBACK_NETWORK_OPTION) while the BOM held the selected
 * option's — and ExecPricingView keys `inConfig`, `inScope` and `hwLineCount`
 * off that Set, so the shipped NICs read as out-of-scope exactly where someone
 * would go to override their price.
 */
export const getAllUniquePartNumbers = (config, productDb, metrics, vendor) => {
  const allParts = getAllActivePartNumbers(config, productDb, metrics, vendor);
  const partNumbers = new Set();
  
  // Extract part numbers from the nested structure
  const extractPartNumbers = (obj) => {
    Object.values(obj).forEach(value => {
      if (typeof value === 'string' && (value.startsWith('VCH-') || value.startsWith('VDP-') || value.startsWith('SVC-'))) {
        partNumbers.add(value);
      } else if (typeof value === 'object' && value !== null) {
        if (value.partNumber) {
          partNumbers.add(value.partNumber);
        } else {
          extractPartNumbers(value);
        }
      }
    });
  };
  
  extractPartNumbers(allParts);
  return Array.from(partNumbers);
};




